// Sections of the expanded product card that mirror the iOS product screen
// (ProductPreviewViewV2): regenerate menu, market value, the remaining
// consolidatedData fields ("custom fields"), metadata, listing actions and
// Mark as Sold. The config-driven field rows stay in ProductListV3.

import React, { useMemo, useState } from 'react';
import {
  Box, Button, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Tooltip,
  IconButton, Typography, Table, TableBody, TableRow, TableCell, TextField, Stack,
  Divider, ButtonBase,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TitleIcon from '@mui/icons-material/Title';
import NotesIcon from '@mui/icons-material/Notes';
import SellIcon from '@mui/icons-material/Sell';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VerifiedIcon from '@mui/icons-material/Verified';
import InlineEditCell from './InlineEditCell';
import {
  marketValueText, displayDateTime, humanizeKey, isPlaceholderValue, listedChannels, channelLabel,
} from './productUtils';

const REGENERATE_OPTIONS = [
  { field: 'all', icon: <AutoAwesomeIcon fontSize="small" />, title: 'Regenerate everything', subtitle: 'New title, description, price and item specifics' },
  { field: 'title', icon: <TitleIcon fontSize="small" />, title: 'Regenerate title', subtitle: 'Get a new title option' },
  { field: 'description', icon: <NotesIcon fontSize="small" />, title: 'Regenerate description', subtitle: 'Create a new description' },
  { field: 'price', icon: <SellIcon fontSize="small" />, title: 'Regenerate price', subtitle: 'Find a new price estimate' },
];

const labelCellSx = { width: { xs: 96, sm: 150 }, color: 'text.secondary', fontSize: '0.72rem', fontWeight: 600, verticalAlign: 'top', pt: 1.25 };

/** Regenerate menu + copy-to-clipboard, shown at the top of the expanded card. */
export function DetailToolbar({ isRegenerating, disabled, onRegenerate, onCopy }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, pt: 1 }}>
      <Button
        size="small"
        variant="outlined"
        disabled={disabled || isRegenerating}
        startIcon={isRegenerating ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
        endIcon={isRegenerating ? null : <ArrowDropDownIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ textTransform: 'none' }}
      >
        {isRegenerating ? 'Regenerating…' : 'Regenerate'}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {REGENERATE_OPTIONS.map((opt) => (
          <MenuItem key={opt.field} onClick={() => { setAnchor(null); onRegenerate(opt.field); }}>
            <ListItemIcon>{opt.icon}</ListItemIcon>
            <ListItemText primary={opt.title} secondary={opt.subtitle} />
          </MenuItem>
        ))}
      </Menu>
      <Box sx={{ flex: 1 }} />
      <Tooltip title="Copy listing details">
        <IconButton size="small" onClick={onCopy}><ContentCopyIcon fontSize="small" /></IconButton>
      </Tooltip>
    </Box>
  );
}

/** "Market Value  $280.00 ($240 – $320) ›" — opens the condition pricing dialog. */
export function MarketValueRow({ product, dirty, onOpen }) {
  const text = marketValueText(product);
  return (
    <ButtonBase
      onClick={onOpen}
      sx={{
        display: 'flex', width: 'calc(100% - 20px)', mx: 1.25, mt: 1, px: 1.25, py: 1, borderRadius: 1.5,
        border: '1px solid', borderColor: dirty ? 'warning.main' : 'divider',
        bgcolor: dirty ? 'rgba(255, 167, 38, 0.14)' : undefined,
        justifyContent: 'space-between', '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Market Value</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{text || 'Not priced yet'}</Typography>
        <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </Box>
    </ButtonBase>
  );
}

// Keys shown elsewhere in the card (headline, market value) or derived by the backend.
const HIDDEN_SPEC_KEYS = new Set(['Title', 'Description', 'Price', 'PriceLow', 'PriceHigh']);
// Category edits need the (currently disabled) recategorize flow — read-only here, like the eBay Category column.
const READ_ONLY_SPEC_KEYS = new Set(['Category', 'PrimaryCategory']);

/**
 * Every consolidatedData field not already covered by a configured column, plus
 * "Show N empty fields" and "Add field" — the iOS "Manage Custom Fields" card.
 * Edits are staged through onFieldEdit like any other cell.
 */
export function OtherFieldsSection({ mergedProduct, edits, coveredFieldPaths, onFieldEdit }) {
  const [showEmpty, setShowEmpty] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newValue, setNewValue] = useState('');

  const keys = useMemo(() => Object.keys(mergedProduct.specs || {})
    .filter((k) => !HIDDEN_SPEC_KEYS.has(k) && !coveredFieldPaths.has(`consolidatedData.${k}`))
    .sort((a, b) => a.localeCompare(b)),
  [mergedProduct.specs, coveredFieldPaths]);

  const emptyKeys = keys.filter((k) => isPlaceholderValue(mergedProduct.specs[k]));
  const visibleKeys = showEmpty ? keys : keys.filter((k) => !isPlaceholderValue(mergedProduct.specs[k]));

  const columnFor = (key) => ({
    id: `spec:${key}`,
    label: humanizeKey(key),
    fieldPath: `consolidatedData.${key}`,
    type: 'text',
    readOnly: READ_ONLY_SPEC_KEYS.has(key),
  });

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    onFieldEdit(columnFor(name), newValue.trim());
    setAdding(false);
    setNewName('');
    setNewValue('');
  };

  return (
    <Box sx={{ pt: 0.5 }}>
      {visibleKeys.length > 0 && (
        <>
          <Divider textAlign="left" sx={{ px: 1.25, '&::before': { width: 0 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>More details</Typography>
          </Divider>
          <Table size="small" sx={{ '& .MuiTableCell-root': { border: 'none' } }}>
            <TableBody>
              {visibleKeys.map((key) => {
                const col = columnFor(key);
                return (
                  <TableRow key={key}>
                    <TableCell sx={labelCellSx}>{col.label}</TableCell>
                    <InlineEditCell
                      column={col}
                      value={mergedProduct.specs?.[key]}
                      product={mergedProduct}
                      onSave={(val) => onFieldEdit(col, val)}
                      dirty={!!edits[col.fieldPath]}
                    />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
      <Box sx={{ display: 'flex', gap: 1, px: 1.25, py: 0.5, flexWrap: 'wrap' }}>
        {emptyKeys.length > 0 && (
          <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setShowEmpty((v) => !v)}>
            {showEmpty ? `Hide ${emptyKeys.length} empty field${emptyKeys.length === 1 ? '' : 's'}` : `Show ${emptyKeys.length} empty field${emptyKeys.length === 1 ? '' : 's'}`}
          </Button>
        )}
        {!adding && (
          <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setAdding(true)}>+ Add field</Button>
        )}
      </Box>
      {adding && (
        <Stack direction="row" spacing={1} sx={{ px: 1.25, pb: 1 }} alignItems="center">
          <TextField size="small" label="Field name" value={newName} autoFocus onChange={(e) => setNewName(e.target.value)} />
          <TextField size="small" label="Value" value={newValue} onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }} />
          <Button size="small" variant="contained" onClick={handleAdd} disabled={!newName.trim()}>Add</Button>
          <Button size="small" onClick={() => { setAdding(false); setNewName(''); setNewValue(''); }}>Cancel</Button>
        </Stack>
      )}
    </Box>
  );
}

/** Created/updated line, listing preview, List Item/Manage Listings, Mark as Sold. */
export function ListingActionsSection({ product, onPreview, onManageListings, onMarkSold }) {
  const live = listedChannels(product);
  const outOfStock = (product.quantity ?? 1) <= 0;
  return (
    <Box sx={{ px: 1.25, pb: 1.25 }}>
      <Typography variant="caption" color="text.disabled" component="div" sx={{ py: 0.75 }}>
        Created {displayDateTime(product.created)}
        {product.updated ? ` · Updated ${displayDateTime(product.updated)}` : ''}
        {product.totalSold ? ` · ${product.totalSold} sold` : ''}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button size="small" variant="outlined" startIcon={<VisibilityIcon fontSize="small" />} onClick={onPreview} sx={{ textTransform: 'none' }}>
          Listing Preview
        </Button>
        <Button size="small" variant="contained" startIcon={<StorefrontIcon fontSize="small" />} onClick={onManageListings} sx={{ textTransform: 'none' }}>
          {live.length ? 'Manage Listings' : 'List Item'}
        </Button>
        <Tooltip title={outOfStock ? 'No quantity remaining' : ''}>
          <span>
            <Button size="small" variant="outlined" color="success" startIcon={<VerifiedIcon fontSize="small" />}
              onClick={onMarkSold} disabled={outOfStock} sx={{ textTransform: 'none' }}>
              Mark as Sold
            </Button>
          </span>
        </Tooltip>
      </Stack>
      {live.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
          Live on {live.length} marketplace{live.length === 1 ? '' : 's'}: {live.map(channelLabel).join(', ')}
        </Typography>
      )}
    </Box>
  );
}
